import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { isPlatformAdmin } from "../../constants/roles";
import "./VulnerabilityCenter.css";

const PAGE_SIZE = 20;

const severityOptions = ["ALL", "CRITICAL", "HIGH", "MEDIUM", "LOW"];

const timeframeOptions = [
  { value: "ALL", label: "All time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];

const VulnerabilityCenter = () => {
  const { user } = useAuth(); // { token, user, loading } but we only need user here
  const navigate = useNavigate();

  const [vulns, setVulns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [severityFilter, setSeverityFilter] = useState("ALL");
  const [timeframe, setTimeframe] = useState("ALL");
  const [search, setSearch] = useState("");

  const [activeSource, setActiveSource] = useState("NVD"); // "NVD" or "CISA_KEV"

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [selectedVuln, setSelectedVuln] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  const [remediation, setRemediation] = useState("");
  const [remediationLoading, setRemediationLoading] = useState(false);
  const [remediationCopied, setRemediationCopied] = useState(false);

  const token = useMemo(
    () => sessionStorage.getItem("auth_token"),
    []
  );

  const totalPages = useMemo(
    () => (total > 0 ? Math.ceil(total / PAGE_SIZE) : 1),
    [total]
  );

  // Fetch vulnerabilities whenever filters / page / tab change
  useEffect(() => {
    const fetchVulns = async () => {
      try {
        if (!token) return;

        setLoading(true);
        setError("");

        const params = new URLSearchParams();
        params.append("page", page);
        params.append("pageSize", PAGE_SIZE);
        params.append("source", activeSource); // NVD vs CISA_KEV

        if (severityFilter !== "ALL") {
          params.append("severity", severityFilter);
        }
        if (timeframe !== "ALL") {
          params.append("timeframe", timeframe);
        }
        if (search.trim()) {
          params.append("search", search.trim());
        }

        const res = await fetch(`/api/vuln/list?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || "Failed to load vulnerabilities");
        }

        const data = await res.json();
        setVulns(data.items || []);
        setTotal(data.total || 0);
      } catch (err) {
        console.error("Error loading vulnerabilities", err);
        setError(err.message || "Error loading vulnerabilities");
      } finally {
        setLoading(false);
      }
    };

    fetchVulns();
  }, [page, severityFilter, timeframe, search, activeSource, token]);

  const openInsights = async (cveId) => {
    try {
      setSelectedVuln(cveId);
      setInsights(null);
      setRemediation("");
      setRemediationCopied(false);
      setInsightsLoading(true);

      const res = await fetch(`/api/vuln/${encodeURIComponent(cveId)}/insights`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to load insights");
      }

      const data = await res.json();
      setInsights(data);
    } catch (err) {
      console.error("Error loading insights", err);
      setInsights({
        error: err.message || "Error loading insights",
      });
    } finally {
      setInsightsLoading(false);
    }
  };

  const closeInsightsPanel = () => {
    setSelectedVuln(null);
    setInsights(null);
    setRemediation("");
    setRemediationCopied(false);
  };

  const generateRemediation = async () => {
    if (!selectedVuln) return;

    try {
      setRemediationLoading(true);
      setRemediation("");
      setRemediationCopied(false);

      const res = await fetch(
        `/api/vuln/${encodeURIComponent(selectedVuln)}/remediation-plan`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to generate remediation plan");
      }

      const data = await res.json();
      setRemediation(data.remediation_plan || "");
    } catch (err) {
      console.error("Error generating remediation plan", err);
      setRemediation(`Error: ${err.message || "Could not generate plan"}`);
    } finally {
      setRemediationLoading(false);
    }
  };

  const handleCopyRemediation = async () => {
    if (!remediation) return;
    try {
      await navigator.clipboard.writeText(remediation);
      setRemediationCopied(true);
      setTimeout(() => setRemediationCopied(false), 1500);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  const handleInventoryClick = () => {
    navigate("/inventory"); // later we’ll build this page
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const handleSeverityChange = (e) => {
    setSeverityFilter(e.target.value);
    setPage(1);
  };

  const handleTimeframeChange = (e) => {
    setTimeframe(e.target.value);
    setPage(1);
  };

  const switchSource = (source) => {
    setActiveSource(source);
    setPage(1);
  };

  
        const affectedAssets = useMemo(() => insights?.affected_assets || [], [insights]);
const affectedAssetCount = useMemo(
  () =>
    typeof insights?.affected_asset_count === "number"
      ? insights.affected_asset_count
      : affectedAssets.length,
  [insights, affectedAssets]
);

  return (
    <div className="vuln-page">
      <div className="vuln-card">
        <div className="vuln-header">
          <div>
            <h1 className="vuln-title">
              Vulnerability Intelligence
              <span className="beta-badge">Beta</span>
            </h1>
            <p className="vuln-subtitle">
              View NIST and CISA vulnerabilities with scores, priorities and insights.
              This module is still under development — features and data may change.
            </p>
          </div>
          {isPlatformAdmin(user?.role) && (
            <button className="vuln-inventory-btn" onClick={handleInventoryClick}>
              Manage Inventory
            </button>
          )}
        </div>

        {/* Tabs: NIST vs CISA KEV */}
        <div className="vuln-tabs">
          <button
            className={
              "vuln-tab-btn" +
              (activeSource === "NVD" ? " vuln-tab-btn-active" : "")
            }
            onClick={() => switchSource("NVD")}
          >
            NIST / NVD
          </button>
          <button
            className={
              "vuln-tab-btn" +
              (activeSource === "CISA_KEV" ? " vuln-tab-btn-active" : "")
            }
            onClick={() => switchSource("CISA_KEV")}
          >
            CISA KEV
          </button>
        </div>

        {/* Filters */}
        <div className="vuln-filters">
          <form onSubmit={handleSearchSubmit} className="vuln-search-form">
            <input
              type="text"
              placeholder="Search by CVE or title"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="vuln-search-input"
            />
            <button type="submit" className="vuln-search-btn">
              Search
            </button>
          </form>

          <div className="vuln-filter-controls">
            <label className="vuln-filter-label">
              Severity
              <select
                value={severityFilter}
                onChange={handleSeverityChange}
                className="vuln-select"
              >
                {severityOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>

            <label className="vuln-filter-label">
              Timeframe
              <select
                value={timeframe}
                onChange={handleTimeframeChange}
                className="vuln-select"
              >
                {timeframeOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {/* Table */}
        <div className="vuln-table-wrapper">
          {loading ? (
            <div className="vuln-center-message">Loading vulnerabilities...</div>
          ) : error ? (
            <div className="vuln-error-message">{error}</div>
          ) : vulns.length === 0 ? (
            <div className="vuln-center-message">
              No vulnerabilities found for the selected filters.
            </div>
          ) : (
            <table className="vuln-table">
              <thead>
                <tr>
                  <th>CVE</th>
                  <th>Title</th>
                  <th>Severity</th>
                  <th>CVSS</th>
                  <th>Published</th>
                  <th>Priority</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
  {vulns.map((v) => {
    const priority =
      v.is_kev && v.severity === "CRITICAL"
        ? "Urgent"
        : v.is_kev
        ? "High"
        : v.severity === "CRITICAL" || v.severity === "HIGH"
        ? "High"
        : v.severity === "MEDIUM"
        ? "Medium"
        : "Low";

    const detailUrl =
      v.detail_url ||
      (v.source === "CISA_KEV"
        ? `https://www.cve.org/CVERecord?id=${encodeURIComponent(
            v.cve_id
          )}`
        : `https://nvd.nist.gov/vuln/detail/${v.cve_id}`);


    return (
      <tr
        key={v.cve_id}
        className="vuln-row-clickable"
        onClick={() => openInsights(v.cve_id)}
      >
        <td className="vuln-cve-cell">
          <a
            href={detailUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()} // 🔸 prevent opening side panel
          >
            {v.cve_id}
          </a>
        </td>
        <td className="vuln-title-cell" title={v.title}>
          {v.title}
        </td>
        <td>
          <span
            className={`vuln-badge vuln-badge-${v.severity?.toLowerCase()}`}
          >
            {v.severity || "-"}
          </span>
        </td>
        <td>{v.cvss_score != null ? v.cvss_score : "-"}</td>
        <td>{v.published_date || "-"}</td>
        <td>
          <span
            className={`vuln-priority vuln-priority-${priority.toLowerCase()}`}
          >
            {priority}
          </span>
        </td>
        <td>
          {/* Optional: keep button if you like, but not required now */}
          <button
            className="vuln-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              openInsights(v.cve_id);
            }}
          >
            Details
          </button>
        </td>
      </tr>
    );
  })}
</tbody>

            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && vulns.length > 0 && (
          <div className="vuln-pagination">
            <button
              className="vuln-page-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span className="vuln-page-info">
              Page {page} of {totalPages}
            </span>
            <button
              className="vuln-page-btn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Side panel for insights + remediation */}
      {selectedVuln && (
        <div className="vuln-side-panel-overlay" onClick={closeInsightsPanel}>
          <div
            className="vuln-side-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="vuln-side-panel-header">
              <div>
                <h2 className="vuln-side-title">{selectedVuln}</h2>
                {insights && !insights.error && (
                  <p className="vuln-side-subtitle">{insights.title}</p>
                )}
              </div>
              <button className="vuln-close-btn" onClick={closeInsightsPanel}>
                ✕
              </button>
            </div>

            <div className="vuln-side-content">
              {insightsLoading && (
                <p className="vuln-side-message">Loading insights...</p>
              )}

              {insights && insights.error && (
                <p className="vuln-error-message">{insights.error}</p>
              )}

              {insights && !insights.error && (
                <>
                  <div className="vuln-side-meta">
                    <div>
                      <span className="vuln-meta-label">Severity</span>
                      <span
                        className={`vuln-badge vuln-badge-${insights.severity?.toLowerCase()}`}
                      >
                        {insights.severity}
                      </span>
                    </div>
                    <div>
                      <span className="vuln-meta-label">CVSS</span>
                      <span>{insights.cvss_score ?? "-"}</span>
                    </div>
                    <div>
                      <span className="vuln-meta-label">Assets</span>
                      <span>{affectedAssetCount}</span>
                    </div>
                  </div>

                  <div className="vuln-side-section">
                    <h3>Affected assets</h3>
                    {affectedAssetCount === 0 ? (
                      <p className="vuln-side-message">
                        No mapped assets yet. Keep your inventory updated for better insights.
                      </p>
                    ) : (
                      <ul className="vuln-asset-list">
                        {affectedAssets.map((a) => (
                          <li key={a.id} className="vuln-asset-item">
                            <div className="vuln-asset-name">
                              {a.name}{" "}
                              {a.environment && (
                                <span className="vuln-asset-chip">
                                  {a.environment}
                                </span>
                              )}
                            </div>
                            <div className="vuln-asset-meta">
                              {a.os_type && <span>{a.os_type}</span>}
                              {a.application_name && (
                                <span>
                                  {a.application_name} {a.application_version}
                                </span>
                              )}
                              {a.business_criticality && (
                                <span>Criticality: {a.business_criticality}</span>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="vuln-side-section">
                    <div className="vuln-side-section-header">
                      <h3>Remediation plan</h3>
                      {remediation && (
                        <button
                          className={
                            "vuln-copy-btn" +
                            (remediationCopied ? " vuln-copy-btn-copied" : "")
                          }
                          onClick={handleCopyRemediation}
                        >
                          {remediationCopied ? "Copied" : "Copy"}
                        </button>
                      )}
                    </div>

                    {!remediation && !remediationLoading && (
                      <button
                        className="vuln-generate-btn vuln-neon-btn"
                        onClick={generateRemediation}
                      >
                        Generate step by step actions
                      </button>
                    )}

                    {remediationLoading && (
                      <p className="vuln-side-message">
                        Generating remediation steps...
                      </p>
                    )}

                    {remediation && (
                      <pre className="vuln-remediation-text">
                        {remediation}
                      </pre>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VulnerabilityCenter;
